import 'leaflet.heat';
import * as L from 'leaflet';
import { Button } from 'antd';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useRef, useState } from 'react';

import 'leaflet/dist/leaflet.css';

import { useFindInformation } from '../../store';
import QueryService from '../../../../services/QueryService';
import { defaultMapConfig, useMapConfig } from "../../../../components/SubjectsMap/helper";
import { CustomIcon } from 'datastake-daf/dist/components';

export const PopUp = ({ data = {} }) => {
  return (
    <div className="popup-header">
      <div className="mr-2">
        <CustomIcon name="MineSite" color="#6C737F" width={14} height={14} />
      </div>

      <div className="cont flex flex-column justify-content-center">
        <h2>{data.name}</h2>
      </div>
    </div>
  )
}

const mapData = () => (d) => {
  const item = d.items[0];
  const gps = item?.datapoints?.find((d) => d.dataId === 'gps')?.data;
  return {
    data: {
      ...item,
      country: item.country,
      sources: d.items.length,
      sourcesList: d.items.map((s) => ({
        ...s.source,
        origin: s.origin,
      })),
    },
    marker: gps && (gps.latitude && gps.longitude) ? {
      latitude: Number(gps.latitude),
      longitude: Number(gps.longitude),
    } : null,
    id: item.id,
  };
}

export default function useMap({ api, container, activeFilters = {} }) {
  const mounted = useRef(true);
  const { t } = useTranslation();
  const [mapRef, setMapRef] = useState();
  const [markers, setMarkers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { TILE_LAYER_URL, MAP_TOKEN } = useMapConfig();
  const [activeMarker, setActiveMarker] = useState(null);

  const roots = useRef([]);
  const abortController = useRef();

  const {
    locations: {
      data,
      form,
      setData,
      fetchForm,
      setSelectOptions,
    }
  } = useFindInformation();

  useEffect(() => () => {
    if (abortController.current) {
      abortController.current.abort();
    }

    mounted.current = false;
  }, []);

  useEffect(() => () => {
    if (mapRef) {
      roots.current.forEach((root) => {
        setTimeout(() => {
          root.unmount();
        });
      })

      roots.current = [];

      mapRef.off();
      mapRef.remove();
    }
  }, [mapRef]);

  const removeMarker = useCallback((id) => {
    if (mapRef) {
      mapRef.eachLayer((layer) => {
        if (id === layer._leaflet_id) {
          mapRef.removeLayer(layer);
        }
      })
    }
  }, [mapRef]);

  // Map control handlers
  const zoomHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() + 1);
    }
  }, [mapRef]);

  const zoomOutHandler = useCallback(() => {
    if (mapRef) {
      mapRef.setZoom(mapRef.getZoom() - 1)
    }
  }, [mapRef]);

  const moveUpHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      // eslint-disable-next-line no-undef
      mapRef.panTo(new L.LatLng(center.lat + value, center.lng));
    }
  }, [mapRef]);

  const moveDownHandler = useCallback(() => {
    if (mapRef) {
      const center = mapRef.getCenter();
      const value = 30 / mapRef.getZoom();
      // eslint-disable-next-line no-undef
      mapRef.panTo(new L.LatLng(center.lat - value, center.lng));
    }
  }, [mapRef]);

  const getMarkerContent = (data = {}) => {
    const div = document.createElement('div');
    const root = createRoot(div);
    root.render(<PopUp data={data} />);
    roots.current.push(root);
    return div;
  };

  const createInstance = () => {
    if (container.current) {
      // eslint-disable-next-line no-undef
      const map = L.map(container.current, {
        ...defaultMapConfig,
        preferCanvas: false,
        center: [-0.02, 37],
      });
      map.fitBounds([[20, 20], [-20, -20]], { padding: [40, 40] });

      // eslint-disable-next-line no-undef
      L.control.scale().addTo(map);
      // eslint-disable-next-line no-undef
      L.tileLayer(TILE_LAYER_URL, {
        access_token: MAP_TOKEN,
        unloadInvisibleTiles: true,
        updateWhenIdle: true,
        reuseTiles: true
      }).addTo(map);
      return map;
    }
  };

  useEffect(() => {
    if (!mapRef) {
      setMapRef(createInstance());
    }
  }, []);

  const fetch = useCallback(async () => {
    const initId = 'loading';
    let dismissed = false;

    api.open({
      message: t('Query'),
      description: t(`Fetching locations`),
      duration: null,
      icon: <LoadingOutlined style={{ color: 'var(--mmt-primary-60)' }} />,
      btn: (
        <Button
          type="link"
          size="small"
          onClick={() => {
            api.destroy(initId);
            dismissed = true;
          }}
        >
          {t('Dismiss')}
        </Button>
      ),
      key: initId,
    });

    try {
      let pages = 1;
      const perPage = 500;
      const id = 'loading';

      await fetchForm();

      const allData = [];
      // eslint-disable-next-line no-undef
      const cluster = L.markerClusterGroup({
        // iconCreateFunction: (cluster) => {
        //   // eslint-disable-next-line no-undef
        //   return L.divIcon({ html: '<b>' + cluster.getChildCount() + '</b>' });
        // }
      });

      setMarkers([cluster])
      mapRef.addLayer(cluster);


      const sendRequest = (page) => new Promise(async (resolve) => {
        try {
          if (!mounted.current) {
            resolve();
            return
          }

          const _abortController = new AbortController();
          abortController.current = _abortController;

          const _data = await QueryService.getLocaitons(page, perPage, activeFilters, _abortController.signal);

          if (!mounted.current) {
            return;
          }

          _data.data.data.map(mapData()).forEach((item) => {
            if (item && item.marker) {
              // eslint-disable-next-line no-undef
              const marker = L.marker([item?.marker?.latitude, item?.marker?.longitude], {
                // eslint-disable-next-line no-undef
                icon: L.divIcon({
                  className: `map-marker icon`,
                  iconSize: [24, 24],
                }),
              });

              // eslint-disable-next-line no-undef
              const popup = L.popup({ closeOnClick: true, autoClose: true })
                .setContent(getMarkerContent(item.data));

              marker.addEventListener('click', () => setActiveMarker(item));
              marker.bindPopup(popup);
              cluster.addLayer(marker);
            }
          })

          allData.push(..._data.data.data);
          const total = _data?.data?.metadata[0]?.total || 0;

          api.destroy(initId);

          if (!dismissed) {
            api.open({
              message: t('Query'),
              description: t(`fetchText`, { length: allData.length, total }),
              duration: null,
              icon: <LoadingOutlined style={{ color: 'var(--mmt-primary-60)' }} />,
              btn: (
                <Button
                  type="link"
                  size="small"
                  // eslint-disable-next-line no-loop-func
                  onClick={() => {
                    api.destroy(id);
                    dismissed = true;
                  }}
                >
                  {t('Dismiss')}
                </Button>
              ),
              key: id,
            });
          }

          pages = Math.ceil(total / perPage);
        } catch (err) {
          console.log(err);
        }

        resolve();
      });

      await sendRequest(1);

      const batches = [];

      for (let i = 1; i < pages; i++) {
        batches.push(i + 1);
      }

      await Promise.all(batches.map((i) => sendRequest(i)));

      api.destroy(id);

      api.success({
        message: t('Query'),
        description: t('Locations fetched successfully')
      });
      setData(allData);
      setLoaded(true);
    } catch (err) {
      console.log(err);
      setData(null);
    }
  }, [fetchForm, activeFilters, api, t, setSelectOptions, markers, removeMarker, mapRef]);

  useEffect(() => {
    setSelectOptions((prev) => ({
      ...prev,
      country: form?.country?.options || [],
      category: form?.category?.options || [],
    }));
  }, [form]);

  useEffect(() => {
    if (mapRef) {
      if (markers.length) {
        roots.current.forEach((root) => {
          setTimeout(() => {
            root.unmount();
          });
        })

        roots.current = [];

        markers.forEach((m) => {
          m.remove();
          removeMarker(m._leaflet_id)
        });
      }

      if (!loaded && data?.length) {
        // eslint-disable-next-line no-undef
        const cluster = L.markerClusterGroup();

        data.map(mapData()).forEach((item) => {
          if (item && item.marker) {
            // eslint-disable-next-line no-undef
            const marker = L.marker([item?.marker?.latitude, item?.marker?.longitude], {
              // eslint-disable-next-line no-undef
              icon: L.divIcon({
                className: `map-marker icon`,
                iconSize: [24, 24],
              }),
            });

            // eslint-disable-next-line no-undef
            const popup = L.popup({ closeOnClick: true, autoClose: true })
              .setContent(getMarkerContent(item.data));

            marker.addEventListener('click', () => setActiveMarker(item));
            marker.bindPopup(popup);
            cluster.addLayer(marker);
          }
        })

        mapRef.addLayer(cluster);
        setMarkers([cluster]);
        setLoaded(true);
      } else {
        if (abortController.current) {
          abortController.current.abort();
        }

        fetch();
      }
    }
  }, [mapRef, activeFilters]);

  return {
    zoomHandler,
    activeMarker,
    moveUpHandler,
    zoomOutHandler,
    setActiveMarker,
    moveDownHandler,
  };
}
